import { z } from 'zod';

const LastMessageSchema = z
  .object({
    authorName: z.string().min(1),
    authorId: z.string().min(1),
    content: z.string(),
    index: z.number().int().nonnegative(),
    date: z.coerce.date(),
  })
  .optional();

const ParticipantSchema = z.object({
  avatar: z.string().url().optional(),
  name: z.string().min(1),
  id: z.string().min(1),
  sid: z.string().min(1),
});

export const ConversationPreviewSchema = z.object({
  lastMessage: LastMessageSchema,
  organizationId: z.string().nullable().optional(),
  scopeKind: z.enum(['direct', 'group']).nullable().optional(),
  participants: z.array(ParticipantSchema).min(1),
  unreadCount: z.number().int().nonnegative(),
  avatar: z.string().url(),
  group: z.boolean(),
  name: z.string().min(1),
  id: z.string().min(1),
});
export type LastMessagePreview = z.infer<typeof LastMessageSchema>;
export type ConversationParticipant = z.infer<typeof ParticipantSchema>;
export type ConversationPreview = z.infer<typeof ConversationPreviewSchema>;

/**
 * Bounds for a participant list on the wire: shape, size and distinctness.
 * What the caller wants to talk to, NOT what it is allowed to talk to —
 * reachability is decided server-side by
 * ChatService.assertParticipantsReachable (and, for a group, the organization
 * membership check on top of it).
 *
 * Ids are uuids because every id in this codebase is a `DataTypes.UUIDV4` PK;
 * 20 is the group-chat ceiling (the caller's own id counts — the frontend
 * posts it alongside the recipients).
 */
const distinctParticipantsId = z
  .array(z.string().uuid())
  .min(1)
  .max(20)
  .refine((ids) => new Set(ids).size === ids.length, {
    message: 'participantsId must not contain duplicate entries',
  });

// One bound for every door that writes a conversation's display name, so a
// name refused at creation cannot be set through the rename route instead.
const chatNameSchema = z.string().trim().min(1).max(120);

export const StartConversationDto = z.object({
  participantsId: distinctParticipantsId,
  chatName: chatNameSchema.optional(),
});
export type ApiStartConversationDto = z.infer<typeof StartConversationDto>;

// A group's name is its display identity (no fallback to a member list the
// way a 1:1 falls back to "the other person"), so it is required here even
// though the 1:1 DTO above leaves chatName optional.
export const StartGroupConversationDto = z.object({
  participantsId: distinctParticipantsId,
  chatName: chatNameSchema,
});
export type ApiStartGroupConversationDto = z.infer<
  typeof StartGroupConversationDto
>;

export const RenameGroupConversationDto = z.object({
  chatName: chatNameSchema,
});
export type ApiRenameGroupConversationDto = z.infer<
  typeof RenameGroupConversationDto
>;

export const AddGroupParticipantDto = z.object({
  participantId: z.string().uuid(),
});
export type ApiAddGroupParticipantDto = z.infer<typeof AddGroupParticipantDto>;

export const MAX_CHAT_ATTACHMENT_BYTES = 15 * 1024 * 1024;
export const MAX_CHAT_BODY_LENGTH = 1600;
export const MAX_CHAT_ATTRIBUTES_LENGTH = 4096;

export const SendChatMessageDto = z
  .object({
    body: z.string().max(MAX_CHAT_BODY_LENGTH).default(''),
    attributes: z
      .string()
      .max(MAX_CHAT_ATTRIBUTES_LENGTH)
      .refine((value) => {
        try {
          const parsed: unknown = JSON.parse(value);
          return (
            parsed !== null &&
            typeof parsed === 'object' &&
            !Array.isArray(parsed)
          );
        } catch {
          return false;
        }
      }, 'Invalid message attributes')
      .optional(),
  })
  .strict();
export type ApiSendChatMessageDto = z.infer<typeof SendChatMessageDto>;
